import React from 'react'
import useShipmentGuide from 'hooks/useShipmentGuide'
import { helpUrl } from 'config'
import { useI18n } from 'i18n'

const STEPS = ['documents', 'items', 'costs', 'result']

// Each step is read from the shipment, so leaving and coming back keeps the
// progress. SKUs can be typed by hand, so having them also clears "documents".
const stepsDone = (shipment, pending) => ({
  documents: shipment.documents.length > 0 || shipment.items.length > 0,
  items: shipment.items.length > 0,
  costs: shipment.costLines.length > 0 && pending === 0,
  result: Boolean(shipment.result?.ok)
})

const CheckIcon = () => (
  <svg aria-hidden viewBox='0 0 24 24' className='h-4 w-4' fill='none' stroke='currentColor' strokeWidth='3' strokeLinecap='round' strokeLinejoin='round'>
    <path d='M5 13l4 4L19 7' />
  </svg>
)

const stepBadgeClass = (done, current) => {
  if (done) return 'bg-status-ready text-white'
  if (current) return 'bg-brand text-white'
  return 'bg-slate-100 text-slate-500'
}

const ShipmentGuide = ({ shipment, pending, activeTab, onGoTo, onCalculate, calculating }) => {
  const { t, locale } = useI18n()
  const done = stepsDone(shipment, pending)
  const { visible, dismiss } = useShipmentGuide(done.result)

  if (!visible) return null

  if (done.result) {
    return (
      <section className='flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white p-4 sm:p-5'>
        <div className='flex items-start gap-3'>
          <span className='flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-status-ready text-white'><CheckIcon /></span>
          <div>
            <h2 className='text-sm font-semibold text-slate-800'>{t('guide.doneTitle')}</h2>
            <p className='mt-0.5 text-sm text-slate-500'>{t('guide.doneBody')}</p>
          </div>
        </div>
        <button onClick={dismiss} className='rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-semibold hover:bg-slate-50'>{t('guide.doneAction')}</button>
      </section>
    )
  }

  const current = STEPS.find((step) => !done[step])
  const doneCount = STEPS.filter((step) => done[step]).length
  const hasErrors = current === 'result' && Boolean(shipment.result)

  return (
    <section aria-labelledby='shipment-guide-title' className='rounded-xl border border-slate-200 bg-white p-4 sm:p-5'>
      <div className='flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1'>
        <h2 id='shipment-guide-title' className='text-sm font-semibold text-slate-800'>{t('guide.title')}</h2>
        <div className='flex items-baseline gap-4 text-sm'>
          <span className='text-slate-500'>{t('guide.progress', { done: doneCount, total: STEPS.length })}</span>
          <button onClick={dismiss} className='font-semibold text-slate-500 hover:text-slate-700 hover:underline'>{t('guide.hide')}</button>
        </div>
      </div>

      <ol className='mt-4 grid gap-2 sm:grid-cols-4'>
        {STEPS.map((step, index) => (
          <li key={step} className={step === current ? undefined : 'hidden sm:block'}>
            <button onClick={() => onGoTo(step)} aria-current={step === current ? 'step' : undefined}
              className={`flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm hover:bg-slate-50 ${step === current ? 'font-semibold text-slate-800' : 'text-slate-500'}`}>
              <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-bold ${stepBadgeClass(done[step], step === current)}`}>
                {done[step] ? <CheckIcon /> : index + 1}
              </span>
              {t(`guide.steps.${step}.title`)}
            </button>
          </li>
        ))}
      </ol>

      <div className='mt-3 flex flex-wrap items-center justify-between gap-3 rounded-lg bg-slate-50 px-3 py-2.5'>
        <p className='min-w-0 flex-1 basis-64 text-sm text-slate-600'>
          {t(hasErrors ? 'guide.steps.result.hintErrors' : `guide.steps.${current}.hint`)}{' '}
          <a href={helpUrl(locale)} target='_blank' rel='noopener noreferrer' className='whitespace-nowrap font-semibold text-brand hover:underline'>{t('guide.manual')}</a>
        </p>
        {current === 'result' && (
          <button onClick={onCalculate} disabled={calculating}
            className='rounded-lg bg-accent px-4 py-2 text-sm font-semibold text-ink hover:bg-accent-600 disabled:opacity-50'>
            {calculating ? t('shipment.calculating') : t('guide.steps.result.action')}
          </button>
        )}
        {current !== 'result' && current !== activeTab && (
          <button onClick={() => onGoTo(current)} className='rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-semibold hover:bg-slate-50'>
            {t(`guide.steps.${current}.action`)}
          </button>
        )}
      </div>
    </section>
  )
}

export default ShipmentGuide
